import { forwardRef } from 'react';
import {
  BECOME_LOGO_SRC,
  IRIS_LOGO_SRC,
  type CertificateSignatureSlot,
  type SignatureId,
} from './courseCertificationDetails';
import './pro-course-default.css';

function certificateSignatureImgClassName(id: SignatureId): string {
  const base = 'pro-course-certificate__signature-img';
  switch (id) {
    case 'lucaBernardelli':
      return `${base} ${base}--lucaBernardelli`;
    case 'gabrieleGramaglia':
      return `${base} ${base}--gabrieleGramaglia`;
    default:
      return base;
  }
}

export type ProCourseDefaultCertificateProps = {
  courseName: string;
  passedDateLabel: string;
  /** Testo sotto la data (durata, provider, ecc.); stringa vuota = nascosto */
  metaLine: string;
  /** Titolo onorifico (opzionale), reso più piccolo del nome */
  participantTitle?: string | null;
  /** Nome e cognome o nome completo (mai email) */
  participantNameLine: string;
  providerLogoUrl?: string | null;
  /** Colonne firma (da resolveCertificateBranding) */
  signatureSlots: CertificateSignatureSlot[];
  /** Mostra logo Iris in basso a destra */
  showIrisLogo: boolean;
};

export const ProCourseDefaultCertificate = forwardRef<
  HTMLDivElement,
  ProCourseDefaultCertificateProps
>(function ProCourseDefaultCertificate(
  {
    courseName,
    passedDateLabel,
    metaLine,
    participantTitle,
    participantNameLine,
    providerLogoUrl,
    signatureSlots,
    showIrisLogo,
  },
  ref
) {
  const hasSignatures = signatureSlots.length > 0;

  return (
    <div
      ref={ref}
      data-certificate-export-root
      className={
        hasSignatures
          ? 'pro-course-certificate pro-course-certificate--has-signatures'
          : 'pro-course-certificate'
      }
    >
      <div className='pro-course-certificate__frame' aria-hidden>
        <div className='pro-course-certificate__frame-edge pro-course-certificate__frame-edge--top certificate-print-color-exact' />
        <div className='pro-course-certificate__frame-edge pro-course-certificate__frame-edge--right certificate-print-color-exact' />
        <div className='pro-course-certificate__frame-edge pro-course-certificate__frame-edge--bottom certificate-print-color-exact' />
        <div className='pro-course-certificate__frame-edge pro-course-certificate__frame-edge--left certificate-print-color-exact' />
      </div>

      {hasSignatures ? (
        <div
          className='pro-course-certificate__footer-rule certificate-print-color-exact'
          aria-hidden
        />
      ) : null}

      {providerLogoUrl ? (
        <div className='pro-course-certificate__top-provider'>
          <img
            className='pro-course-certificate__logo-img'
            src={providerLogoUrl}
            alt=''
            crossOrigin='anonymous'
          />
        </div>
      ) : null}

      <div className='pro-course-certificate__body'>
        <p className='pro-course-certificate__kicker'>
          Corso di alta formazione in
        </p>

        <h2 className='pro-course-certificate__course-title'>{courseName}</h2>

        <p className='pro-course-certificate__cert-type'>
          CERTIFICATO DI COMPETENZA
        </p>

        <p className='pro-course-certificate__participant'>
          {participantTitle ? (
            <span className='pro-course-certificate__participant-title mr-dafoe-regular'>
              {participantTitle}
            </span>
          ) : null}
          <span className='pro-course-certificate__participant-name mr-dafoe-regular'>
            {participantNameLine}
          </span>
        </p>

        <p className='pro-course-certificate__date'>{passedDateLabel}</p>

        {metaLine ? (
          <p className='pro-course-certificate__meta'>{metaLine}</p>
        ) : null}
      </div>

      {signatureSlots.length > 0 ? (
        <div className='pro-course-certificate__signatures'>
          {signatureSlots.map(slot => (
            <div
              key={slot.id}
              className='pro-course-certificate__signature-col'
            >
              <div className='pro-course-certificate__signature-img-wrap'>
                <img
                  className={certificateSignatureImgClassName(slot.id)}
                  src={slot.imageSrc}
                  alt=''
                />
              </div>
              <p className='pro-course-certificate__signature-name'>
                {slot.nameLine}
              </p>
              <p className='pro-course-certificate__signature-role'>
                {slot.roleLine}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <div className='pro-course-certificate__brand' aria-label='BECOME'>
        <img
          className='pro-course-certificate__brand-logo'
          src={BECOME_LOGO_SRC}
          alt=''
          crossOrigin='anonymous'
        />
      </div>

      {showIrisLogo ? (
        <img
          className='pro-course-certificate__iris-logo'
          src={IRIS_LOGO_SRC}
          alt=''
        />
      ) : null}
    </div>
  );
});
